/**
 * Shared non-negative input sanitizers.
 *
 * Reps / sets / weight / RPE can never be negative. These helpers are applied
 * at every typed input surface so a "-" can't be entered at all, and steppers
 * clamp at 0 instead of decrementing past it.
 */

/** Digits only — no sign, no decimal separator. */
export function sanitizeNonNegativeInt(raw: string, maxLen = 20): string {
  return raw.replace(/[^0-9]/g, "").slice(0, maxLen);
}

/** Digits + a single decimal separator (comma normalised to dot). No sign. */
export function sanitizeNonNegativeDecimal(raw: string, maxLen = 20): string {
  const cleaned = raw.replace(/,/g, ".").replace(/[^0-9.]/g, "");
  const first = cleaned.indexOf(".");
  const single =
    first === -1 ? cleaned : cleaned.slice(0, first + 1) + cleaned.slice(first + 1).replace(/\./g, "");
  return single.slice(0, maxLen);
}

/**
 * Rep *labels* may legitimately contain a range separator ("8-12"), so we only
 * strip a leading minus (and any minus directly after a separator) rather than
 * banning the character outright.
 */
export function sanitizeRepsLabel(raw: string, maxLen = 20): string {
  return raw.replace(/^[-\s]+/, "").replace(/([-–/,x×]\s*)-+/g, "$1").slice(0, maxLen);
}

/** Clamp a numeric value to >= 0 (used by +/- steppers and before persisting). */
export function clampMin0(n: number): number {
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Server-side guard for free-text sets/reps labels: digits, ranges and simple
 * separators are fine ("8-12", "3 x 10", "AMRAP"), a leading/standalone minus
 * that would express a negative count is not.
 */
export const NON_NEGATIVE_LABEL = /^(?!\s*-)(?!.*[([{:=,]\s*-\s*\d)[^\n]*$/;
